// /apply/<code>: the founding developers' invite flow (docs/build/founding-devs.md "The flow"). One question per
// screen, phone first, under two minutes. Screens 1–6 stay in this browser (src/dev-apply.ts) until Send. Send goes
// through the existing sign-in (/signin, GitHub, Discord or email), and a signed-in person just presses Send. The
// result shows their inventory and what happens next, never how they rank.
//
// Each screen is `?s=<n>` in the address, so the phone's back gesture steps back through the flow.

import { api, ApiError, isMissingEndpoint, loadTokens } from "../api.ts";
import { artUrl, getCatalogue, type Catalogue, type Game } from "../catalogue.ts";
import { coverStyle } from "../components.ts";
import { creatorAgeCopy, CREATOR_AGE } from "../creator-age.ts";
import {
  answered,
  applicationGames,
  draftFrom,
  FOUNDING_BY,
  inviteGames,
  PUT_ERRORS,
  replyBy,
  answerLines,
  APPLY_OPTIONS,
  aboutProblem,
  asInviteGame,
  clearDraft,
  DISCLOSURE,
  dayText,
  domainOf,
  emptyDraft,
  foundingLine,
  furthest,
  inventoryLine,
  LIMITS,
  loadDraft,
  matchUrls,
  MAX_URLS,
  RESULT,
  SAVE,
  saveDraft,
  searchGames,
  STATUS_COPY,
  submission,
  toggleCreator,
  URL_STATE,
  yoursTiles,
  type Application,
  type Draft,
  type Cohort,
  type InviteView,
  type InviteGame,
  type MyApplication,
} from "../dev-apply.ts";
import { clear, errorBox, h, link } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { selling } from "../selling.ts";
import { loadMe, nameOf, type Me } from "../session.ts";
import { getMethods, PROVIDER_NAME, startProvider, type Provider } from "./me-methods.ts";

/** The invite as the screens read it: accounts' answer with the games flattened, each carrying its creator. */
interface Invite {
  code: string;
  name: string;
  xHandle: string;
  used: boolean;
  creators: InviteView["creators"];
  /** the claimable creators' games (one someone else holds isn't offered) */
  games: InviteGame[];
  invitedBy: string | null;
  /** the live count: on the invite, and on GET /dev-applications/me for a signed-in person */
  cohort: Cohort | null;
}

const fromView = (v: InviteView): Invite => ({ ...v.invite, invitedBy: v.invite.invitedBy ?? null, creators: v.creators, games: inviteGames({ creators: v.creators.filter((c) => c.claimable) }), cohort: v.cohort ?? null });

/** The invite, once per code per page load (each screen is a re-render). */
let cached: { code: string; invite: Promise<InviteView> } | null = null;
const inviteFor = (code: string) => {
  if (cached?.code !== code) cached = { code, invite: api<InviteView>("accounts", `/dev-invites/${encodeURIComponent(code)}`) };
  cached.invite.catch(() => (cached = null));
  return cached.invite;
};

/** GET /dev-applications/me once per user per page load (null: not applied, or accounts without the route). */
let mineFor: { id: string; p: Promise<MyApplication | null> } | null = null;
const myApplication = (userId: string) => {
  if (mineFor?.id !== userId) mineFor = { id: userId, p: api<MyApplication>("accounts", "/dev-applications/me").catch(() => null) };
  return mineFor.p;
};

/** The result after Send, kept for the re-render that `?s=8` triggers. */
let sent: { code: string; mine: MyApplication } | null = null;

const href = (code: string, s: number) => `/apply/${encodeURIComponent(code)}${s ? `?s=${s}` : ""}`;

export async function applyPage(params: Record<string, string>, query: URLSearchParams): Promise<HTMLElement> {
  setTitle("Founding developers");
  const code = params.code ?? "";
  let view: InviteView;
  try {
    view = await inviteFor(code);
  } catch (e) {
    return shell(null, notFound(e));
  }
  const invite = fromView(view);
  const [cat, me] = await Promise.all([getCatalogue().catch(() => ({ games: [], creators: [] }) as unknown as Catalogue), loadMe().catch(() => null)]);

  // already applied (this browser just sent it, or they come back later): the result, not the questions again
  const asked = Number(query.get("s") ?? 0);
  let mine = sent?.code === code ? sent.mine : null;
  if (!mine && me && loadTokens()) mine = await myApplication(me.id);
  invite.cohort = mine?.cohort ?? invite.cohort;
  if (mine?.application && (asked === RESULT || !loadDraft(code))) return shell(invite, result(invite, mine, mine.application, cat), RESULT);

  if (invite.used && !mine?.application) return shell(invite, gone("This invite has been used", "If that was you, sign in with the same account to see your application.", link(`/signin?next=${encodeURIComponent(href(code, 0))}`, { class: "btn" }, "Sign in")));

  // creators are 18+: a signed-in 13-17 account hears it first, not after six screens
  if (me && loadTokens() && me.ageBand !== "18+") return shell(invite, ageNote());
  const d = loadDraft(code) ?? emptyDraft(code, view);
  const step = Math.max(0, Math.min(asked, furthest(d), SAVE));
  if (step !== asked && asked) history.replaceState(null, "", href(code, step));
  d.step = step;
  d.reached = Math.max(d.reached, step);
  saveDraft(d);
  const go = (n: number) => {
    saveDraft(d);
    navigate(href(code, n));
  };
  const screen =
    step === 0 ? hello(invite, go)
    : step === 1 ? yours(invite, d, cat, go)
    : step === 2 ? addresses(d, cat, go)
    : step === 3 ? discovery(d, go)
    : step === 4 ? whatsNext(d, go)
    : step === 5 ? money(d, go)
    : step === 6 ? about(d, go)
    : save(invite, d, cat, me);
  return shell(invite, screen, step, step > 0 ? () => go(step - 1) : null);
}

// ---- the frame: progress, back, and one screen --------------------------------------------------------------------

function shell(invite: Invite | null, screen: HTMLElement, step = 0, back: (() => void) | null = null): HTMLElement {
  const questions = SAVE; // 1–7 fill the bar; the hello and the result have none
  const showBar = step >= 1 && step <= SAVE;
  return h(
    "div",
    { class: `apply apply-s${step}`, "data-step": step },
    // the hello and the result carry their own kicker: no bar above them
    !showBar ? null : h(
      "div",
      { class: "apply-top" },
      back ? h("button", { type: "button", class: "apply-back", onclick: back, "aria-label": "Back" }, "←") : null,
      showBar
        ? h(
            "div",
            { class: "apply-progress", role: "progressbar", "aria-label": "Progress", "aria-valuemin": 1, "aria-valuemax": questions, "aria-valuenow": step },
            Array.from({ length: questions }, (_, i) => h("span", { class: i < step ? "on" : null })),
          )
        : null,
      invite && showBar ? h("span", { class: "apply-who" }, invite.name) : null,
    ),
    screen,
  );
}

/** A screen: the question, an optional line under it, the body, and the action bar (sticky on a phone). */
function screen(question: string, sub: string | null, body: (HTMLElement | null)[], actions: (HTMLElement | null)[]): HTMLElement {
  const title = h("h1", { class: "apply-q", tabindex: "-1" }, question);
  queueMicrotask(() => title.isConnected && title.focus({ preventScroll: true }));
  return h("section", { class: "apply-screen" }, title, sub ? h("p", { class: "apply-sub" }, sub) : null, ...body, h("div", { class: "apply-actions" }, ...actions));
}

const next = (label: string, ok: () => boolean, onclick: () => void) => {
  const b = h("button", { type: "button", class: "btn apply-next", onclick: () => ok() && onclick() }, label);
  return b;
};

// ---- 0: hello ------------------------------------------------------------------------------------------------------

function hello(invite: Invite, go: (n: number) => void): HTMLElement {
  const name = invite.name.replace(/^@/, "");
  const first = name.split(/\s+/)[0] || name;
  const count = foundingLine(invite.cohort);
  return h(
    "section",
    { class: "apply-screen apply-hello" },
    h("div", { class: "apply-hello-art", "aria-hidden": "true" }),
    h("p", { class: "apply-kicker" }, "Founding developers"),
    h("h1", { class: "apply-q apply-hi", tabindex: "-1" }, `Hi ${first}.`),
    h("p", { class: "apply-lead" }, invite.invitedBy ? `${invite.invitedBy} asked you here. It takes two minutes.` : "You're invited. It takes two minutes."),
    invite.games.length
      ? h("p", { class: "apply-sub" }, `We think ${invite.games.length === 1 ? "this game is" : `these ${invite.games.length} games are`} yours. Check them, tell us what's next, and you're done.`)
      : h("p", { class: "apply-sub" }, "Show us your games, tell us what's next, and you're done."),
    invite.games.length ? h("div", { class: "apply-hello-strip", "aria-hidden": "true" }, invite.games.slice(0, 4).map((g) => h("span", { class: "apply-hello-cover", style: artStyle(g) }))) : null,
    invite.cohort || count
      ? h(
          "p",
          { class: "apply-count" },
          h("span", { class: "apply-dot", "aria-hidden": "true" }),
          h("span", null, `${invite.cohort?.capacity ?? 50} founding developers by ${dayText(Date.parse(`${FOUNDING_BY}T12:00:00Z`) / 1000).replace(/^\w+ /, "")}`, count ? [h("br"), h("span", { class: "apply-count-live" }, count)] : null),
        )
      : null,
    h("div", { class: "apply-actions" }, h("button", { type: "button", class: "btn apply-next", onclick: () => go(1) }, "Start")),
  );
}

// ---- 1: these are yours? ------------------------------------------------------------------------------------------

function artStyle(g: Pick<InviteGame, "art"> & { id?: string }, cat?: Pick<Catalogue, "games">): Partial<CSSStyleDeclaration> {
  const full = g.id && cat ? cat.games.find((x) => x.id === g.id) : undefined;
  if (full) return coverStyle(full);
  const art = artUrl(g.art);
  return { backgroundImage: art ? `url("${art}"), linear-gradient(160deg, #2a2320, #0e0d0c)` : "linear-gradient(160deg, #2a2320, #0e0d0c)" };
}

function tile(g: InviteGame, on: boolean, cat: Pick<Catalogue, "games">, toggle: () => void, creatorName?: string): HTMLElement {
  return h(
    "button",
    { type: "button", class: "apply-tile", "aria-pressed": on ? "true" : "false", onclick: toggle },
    h("span", { class: "apply-tile-art", style: artStyle(g, cat) }),
    h("span", { class: "apply-tile-tick", "aria-hidden": "true" }, on ? "✓" : ""),
    h("span", { class: "apply-tile-name" }, g.title),
    creatorName ? h("span", { class: "apply-tile-by" }, creatorName) : null,
  );
}

function yours(invite: Invite, d: Draft, cat: Catalogue, go: (n: number) => void): HTMLElement {
  const found = d.foundGameIds.map((id) => cat.games.find((g) => g.id === id)).filter((g): g is Game => !!g).map(asInviteGame);
  const grid = h("div", { class: "apply-tiles" });
  const nameOfCreator = (id: string) => invite.creators.find((c) => c.id === id)?.name ?? cat.creators.find((c) => c.id === id)?.name;
  const draw = () => {
    const tiles = yoursTiles(invite.games, found);
    clear(
      grid,
      tiles.length
        ? tiles.map((g) =>
            tile(g, d.creatorIds.includes(g.creatorId), cat, () => {
              d.creatorIds = toggleCreator(d.creatorIds, g.creatorId);
              saveDraft(d);
              draw();
            }, nameOfCreator(g.creatorId)),
          )
        : h("p", { class: "apply-empty" }, "We haven't matched any listed games to you yet. Search below, or add addresses on the next screen."),
    );
    count.textContent = d.creatorIds.length ? `${tiles.filter((g) => d.creatorIds.includes(g.creatorId)).length} ticked` : "";
  };
  const count = h("span", { class: "apply-ticked", "aria-live": "polite" });
  const results = h("div", { class: "apply-results", "aria-live": "polite" });
  const search = h("input", {
    type: "search",
    class: "apply-search",
    placeholder: "Search by game or studio",
    "aria-label": "Search games on Banger",
    autocomplete: "off",
    oninput: () => {
      const hits = searchGames(cat.games, search.value).filter((g) => !yoursTiles(invite.games, found).some((t) => t.id === g.id));
      clear(
        results,
        search.value.trim().length >= 2 && !hits.length ? h("p", { class: "apply-hint" }, "Not on Banger yet? Add its address on the next screen.") : null,
        hits.map((g) =>
          h(
            "button",
            {
              type: "button",
              class: "apply-hit",
              onclick: () => {
                found.push(asInviteGame(g));
                d.foundGameIds = [...new Set([...d.foundGameIds, g.id])];
                if (!d.creatorIds.includes(g.creator.id)) d.creatorIds = [...d.creatorIds, g.creator.id];
                saveDraft(d);
                search.value = "";
                clear(results);
                draw();
              },
            },
            h("span", { class: "apply-hit-art", style: coverStyle(g) }),
            h("span", { class: "apply-hit-text" }, h("strong", null, g.title), h("span", null, g.creator.name)),
            h("span", { class: "apply-hit-add" }, "Add"),
          ),
        ),
      );
    },
  });
  draw();
  return screen(
    invite.games.length ? "These are yours?" : "Which games are yours?",
    invite.games.length ? "We matched these to you. Untick anything that isn't." : null,
    [
      grid,
      count,
      h("details", { class: "apply-more", open: invite.games.length === 0 }, h("summary", null, "Something missing? Search Banger"), search, results),
    ],
    [next("Continue", () => true, () => go(2))],
  );
}

// ---- 2: anything else out there? ----------------------------------------------------------------------------------

function addresses(d: Draft, cat: Catalogue, go: (n: number) => void): HTMLElement {
  const list = h("ul", { class: "apply-urls", "aria-live": "polite" });
  const area = h("textarea", {
    class: "apply-paste",
    rows: 4,
    placeholder: "yourgame.com\nyou.itch.io/another-game",
    "aria-label": `Game addresses, one per line, up to ${MAX_URLS}`,
    autocapitalize: "off",
    autocomplete: "off",
    spellcheck: "false",
    oninput: () => {
      d.urlsText = area.value;
      saveDraft(d);
      draw();
    },
  }) as HTMLTextAreaElement;
  area.value = d.urlsText;
  const btn = next("Skip", () => true, () => go(3));
  const draw = () => {
    const { lines, over } = matchUrls(d.urlsText, cat.games);
    btn.textContent = lines.length ? "Continue" : "Skip";
    clear(
      list,
      lines.map((l) => {
        if (l.problem === "not_an_address") return h("li", { class: "apply-url bad" }, h("span", { class: "apply-url-domain" }, l.raw), h("span", { class: "apply-url-state" }, "Not a web address"));
        if (l.problem === "duplicate") return null;
        if (l.game) {
          const g = l.game;
          const mine = d.creatorIds.includes(g.creatorId);
          return h(
            "li",
            { class: "apply-url listed" },
            h("span", { class: "apply-hit-art", style: artStyle(g, cat) }),
            h("span", { class: "apply-hit-text" }, h("strong", null, mine ? `That's ${g.title}. Already in your list.` : `That's ${g.title}. Yours?`), h("span", null, `On Banger, by ${g.creatorName}`)),
            mine
              ? h("span", { class: "apply-url-ok", "aria-label": "In your list" }, "✓")
              : h("button", { type: "button", class: "btn small", onclick: () => ((d.creatorIds = toggleCreator(d.creatorIds, g.creatorId)), saveDraft(d), draw()) }, "Yes"),
          );
        }
        return h("li", { class: "apply-url new" }, h("span", { class: "apply-url-fav", "aria-hidden": "true" }, domainOf(l.raw).slice(0, 1).toUpperCase()), h("span", { class: "apply-hit-text" }, h("strong", { class: "apply-url-domain" }, domainOf(l.raw)), h("span", null, "New: we add it after review")));
      }),
      over ? h("li", { class: "apply-url bad" }, `${over} more than ${MAX_URLS}: send the rest later from your dashboard.`) : null,
    );
  };
  draw();
  return screen("Anything else out there?", `Paste your other game addresses, one per line. Up to ${MAX_URLS}.`, [area, list], [btn]);
}

// ---- 3–5: their situation ------------------------------------------------------------------------------------------

function choices<T extends string>(name: string, opts: readonly (readonly [T, string])[], isOn: (v: T) => boolean, set: (v: T) => void, multi: boolean): HTMLElement {
  const box = h("div", { class: "apply-choices", role: multi ? "group" : "radiogroup", "aria-label": name });
  const draw = () =>
    clear(
      box,
      opts.map(([v, label]) =>
        h(
          "button",
          { type: "button", class: "apply-choice", role: multi ? "checkbox" : "radio", "aria-checked": isOn(v) ? "true" : "false", onclick: () => (set(v), draw()) },
          h("span", { class: `apply-choice-box${multi ? "" : " round"}`, "aria-hidden": "true" }, isOn(v) ? "✓" : ""),
          label,
        ),
      ),
    );
  draw();
  return box;
}

function discovery(d: Draft, go: (n: number) => void): HTMLElement {
  const btn = next("Continue", () => answered(d, 3), () => go(4));
  const sync = () => btn.toggleAttribute("disabled", !answered(d, 3));
  const box = choices("How players find your games", APPLY_OPTIONS.discovery, (v) => d.answers.discovery.includes(v), (v) => {
    const on = d.answers.discovery.includes(v);
    // "they mostly don't" stands alone
    d.answers.discovery = on ? d.answers.discovery.filter((x) => x !== v) : v === "mostly_dont" ? ["mostly_dont"] : [...d.answers.discovery.filter((x) => x !== "mostly_dont"), v];
    saveDraft(d);
    sync();
  }, true);
  sync();
  return screen("How do players find your games today?", "Pick any.", [box], [btn]);
}

function whatsNext(d: Draft, go: (n: number) => void): HTMLElement {
  const btn = next("Continue", () => answered(d, 4), () => go(5));
  const sync = () => btn.toggleAttribute("disabled", !answered(d, 4));
  const what = h("input", {
    type: "text",
    class: "apply-line",
    maxlength: LIMITS.nextWhat,
    placeholder: "A racing game, a jam entry, an update…",
    "aria-label": "What you're working on",
    value: d.answers.next.what,
    oninput: () => ((d.answers.next.what = what.value), saveDraft(d), sync()),
  }) as HTMLInputElement;
  const when = choices("When", APPLY_OPTIONS.when, (v) => d.answers.next.when === v, (v) => {
    d.answers.next.when = v;
    saveDraft(d);
    sync();
    // with the line written, one tap answers it: move on once the tick has shown
    if (answered(d, 4)) setTimeout(() => when.isConnected && go(5), 260);
    else what.focus();
  }, false);
  sync();
  return screen("What's next?", "One line on what you're working on, and when it ships.", [what, h("p", { class: "apply-label" }, "It ships…"), when], [btn]);
}

function money(d: Draft, go: (n: number) => void): HTMLElement {
  const btn = next("Continue", () => answered(d, 5), () => go(6));
  const box = choices("Money so far", APPLY_OPTIONS.money, (v) => d.answers.money === v, (v) => {
    d.answers.money = v;
    saveDraft(d);
    btn.removeAttribute("disabled");
    // one tap answers it: move on once the tick has shown
    setTimeout(() => box.isConnected && go(6), 260);
  }, false);
  if (!answered(d, 5)) btn.setAttribute("disabled", "");
  return screen("Has a web game earned you money?", null, [box], [btn]);
}

// ---- 6: about you ------------------------------------------------------------------------------------------------

function about(d: Draft, go: (n: number) => void): HTMLElement {
  const err = h("p", { class: "apply-error", role: "alert", "aria-live": "polite" });
  const field = (id: string, label: string, input: HTMLInputElement, hint?: string) => h("div", { class: "apply-field" }, h("label", { for: id }, label), input, hint ? h("span", { class: "apply-hint" }, hint) : null);
  const bind = (id: string, key: "displayName" | "xHandle" | "site", attrs: Record<string, string | number>) => {
    const el = h("input", { id, ...attrs, value: d.about[key], oninput: () => ((d.about[key] = el.value), saveDraft(d), clear(err)) }) as HTMLInputElement;
    return el;
  };
  const name = bind("apply-name", "displayName", { type: "text", autocomplete: "name", maxlength: 60 });
  const x = bind("apply-x", "xHandle", { type: "text", autocomplete: "off", autocapitalize: "off", spellcheck: "false", placeholder: "@yourname" });
  const site = bind("apply-site", "site", { type: "url", inputmode: "url", autocomplete: "url", autocapitalize: "off", placeholder: "yoursite.com" });
  const studio = choices("Solo or studio", APPLY_OPTIONS.studio, (v) => d.about.studio === v, (v) => ((d.about.studio = v), saveDraft(d)), false);
  return screen(
    "About you",
    null,
    [
      field("apply-name", "The name you make games under", name),
      field("apply-x", "X handle", x),
      field("apply-site", "Your site (optional)", site),
      studio,
      h("p", { class: "apply-fine" }, DISCLOSURE),
      err,
    ],
    [
      next(
        "Continue",
        () => {
          const p = aboutProblem(d.about);
          if (p) clear(err, p);
          return !p;
        },
        () => go(SAVE),
      ),
    ],
  );
}

// ---- 7: save it ---------------------------------------------------------------------------------------------------

function save(invite: Invite, d: Draft, cat: Catalogue, me: Me | null): HTMLElement {
  const s = submission(d, cat);
  const listed = cat.games.filter((g) => s.creatorIds.includes(g.creator.id));
  const games = listed.length || invite.games.filter((g) => s.creatorIds.includes(g.creatorId)).length;
  // what they're about to send, as covers: the reward for the questions before the account
  const strip = listed.length ? h("div", { class: "apply-hello-strip apply-save-strip", "aria-hidden": "true" }, listed.slice(0, 5).map((g) => h("span", { class: "apply-hello-cover", style: coverStyle(g) }))) : null;
  const summary = h(
    "ul",
    { class: "apply-summary" },
    h("li", null, h("strong", null, String(games)), games === 1 ? " listed game" : " listed games"),
    h("li", null, h("strong", null, String(s.urls.length)), s.urls.length === 1 ? " new address" : " new addresses"),
    h("li", null, h("strong", null, s.xHandle ? `@${s.xHandle}` : "–"), " on X"),
  );
  const out = h("div", { "aria-live": "polite" });
  const back = `${href(invite.code, SAVE)}`;

  if (me && loadTokens()) {
    if (me.ageBand !== "18+") return ageNote();
    const send = h("button", { type: "button", class: "btn apply-next apply-send" }, "Send");
    send.addEventListener("click", async () => {
      send.setAttribute("disabled", "");
      send.textContent = "Sending…";
      clear(out);
      try {
        const mine = await api<MyApplication>("accounts", "/dev-applications/me", { method: "PUT", body: s });
        clearDraft();
        sent = { code: invite.code, mine };
        mineFor = null;
        navigate(href(invite.code, RESULT), true);
      } catch (e) {
        // creators are 18+ (docs/build/creator-age.md): the same note the dashboard shows, not a new step
        if (e instanceof ApiError && e.code === CREATOR_AGE) return void document.querySelector(".apply-screen")?.replaceWith(ageNote());
        send.removeAttribute("disabled");
        send.textContent = "Send";
        const words = e instanceof ApiError ? PUT_ERRORS[e.code] : undefined;
        clear(out, words ? h("p", { class: "apply-error", role: "alert" }, words) : errorBox(e));
      }
    });
    return screen("Save it", `Signed in as ${nameOf(me)}. Send it and you're done.`, [strip, summary, out], [send]);
  }

  // signed out: the existing sign-in, which brings them back here (the draft waits in this browser)
  const providers = h("div", { class: "apply-providers" });
  const email = link(`/signin?next=${encodeURIComponent(back)}`, { class: "btn ghost apply-email" }, "Continue with email");
  void getMethods()
    .then((m) => {
      const ps = (["github", "discord"] as Provider[]).filter((p) => m[p]);
      clear(
        providers,
        ps.map((p) =>
          h(
            "button",
            {
              type: "button",
              class: `btn apply-provider ${p}`,
              onclick: async (e: Event) => {
                const b = e.currentTarget as HTMLButtonElement;
                b.setAttribute("disabled", "");
                try {
                  await startProvider(p, { next: back });
                } catch (x) {
                  b.removeAttribute("disabled");
                  clear(out, errorBox(x));
                }
              },
            },
            `Continue with ${PROVIDER_NAME[p]}`,
          ),
        ),
      );
    })
    .catch(() => {});
  return screen(
    "Save it",
    "Make your Banger account to send this. You'll come straight back here.",
    [strip, summary, out],
    [providers, email, h("p", { class: "apply-fine" }, "Creators on Banger are 18+.")],
  );
}

function ageNote(): HTMLElement {
  const c = creatorAgeCopy(selling());
  return h(
    "section",
    { class: "apply-screen creator-age" },
    h("h1", { class: "apply-q" }, c.title),
    h("p", { class: "apply-lead" }, c.lead),
    h("p", { class: "apply-sub" }, c.later),
    h("p", { class: "apply-sub" }, c.meanwhile),
    h("div", { class: "apply-actions" }, link("/browse", { class: "btn apply-next" }, "Browse games")),
  );
}

// ---- 8: the result ------------------------------------------------------------------------------------------------

function result(invite: Invite, mine: MyApplication, a: Application, cat: Catalogue): HTMLElement {
  const copy = STATUS_COPY[a.status];
  const tiles = applicationGames(a);
  const adding = a.urls.filter((u) => u.state === "being_added");
  const lines = answerLines(a.answers);
  const count = foundingLine(mine.cohort);
  return h(
    "section",
    { class: "apply-screen apply-result" },
    h("div", { class: "apply-seal", "aria-hidden": "true" }, a.status === "declined" ? "·" : "✓"),
    h("p", { class: "apply-kicker" }, copy.badge),
    h("h1", { class: "apply-q", tabindex: "-1" }, a.status === "submitted" ? "Sent. Thank you." : copy.title),
    h("p", { class: "apply-lead" }, inventoryLine(tiles.length, adding.length)),
    tiles.length || adding.length
      ? h(
          "div",
          { class: "apply-tiles small" },
          tiles.map((g) => h("div", { class: "apply-tile static" }, h("span", { class: "apply-tile-art", style: artStyle(g, cat) }), h("span", { class: "apply-tile-name" }, g.title))),
          adding.map((u) => h("div", { class: "apply-tile static adding" }, h("span", { class: "apply-tile-art blank" }, h("span", null, domainOf(u.url).slice(0, 1).toUpperCase())), h("span", { class: "apply-tile-name" }, domainOf(u.url)), h("span", { class: "apply-tile-by" }, URL_STATE[u.state]))),
        )
      : null,
    lines.length ? h("ul", { class: "apply-lines" }, lines.map((l) => h("li", null, l.text))) : null,
    h(
      "div",
      { class: "apply-next-box" },
      h("p", null, h("strong", null, a.status === "submitted" ? `You'll hear from us by ${dayText(replyBy(a.createdAt))}.` : copy.title + "."), " ", copy.body),
      a.note && a.status !== "submitted" ? h("p", { class: "apply-sub" }, a.note) : null,
      count ? h("p", { class: "apply-count" }, h("span", { class: "apply-dot", "aria-hidden": "true" }), count) : null,
    ),
    h(
      "div",
      { class: "apply-actions" },
      link("/dashboard", { class: "btn apply-next" }, "Your dashboard"),
      a.status === "waitlisted" ? link("/haunt", { class: "btn ghost" }, "Open Haunt") : null,
      a.status === "submitted"
        ? h(
            "button",
            {
              type: "button",
              class: "btn ghost",
              onclick: () => {
                // answers are editable until decided: back to screen 1 with what they sent
                saveDraft(draftFrom(invite.code, a));
                sent = null;
                navigate(href(invite.code, 1));
              },
            },
            "Change your answers",
          )
        : null,
    ),
  );
}

// ---- links that don't work -----------------------------------------------------------------------------------------

function notFound(e: unknown): HTMLElement {
  if (e instanceof ApiError && (e.code === "invite_not_found" || isMissingEndpoint(e)))
    return gone("This invite link doesn't work", "Check it came through whole, or reply to the message it came in.");
  if (e instanceof ApiError && e.code === "invite_expired") return gone("This invite has expired", "Reply to the message it came in, and we'll send you a fresh one.");
  return h("section", { class: "apply-screen" }, h("h1", { class: "apply-q" }, "We couldn't open your invite"), errorBox(e));
}

function gone(title: string, body: string, action?: HTMLElement): HTMLElement {
  return h(
    "section",
    { class: "apply-screen" },
    h("h1", { class: "apply-q" }, title),
    h("p", { class: "apply-sub" }, body),
    h("div", { class: "apply-actions" }, action ?? link("/browse", { class: "btn apply-next" }, "Browse games")),
  );
}
